/**
 * Privacy and safety (user-management §2.9, the P1 rows): DMs from server
 * members, message requests, who may send friend requests and Pokes from.
 * The values are the server's (`settings.privacy`); a change
 * shows once the server saved it, and a refusal puts the old value back.
 */
import type { PokesFrom, PrivacySettings } from '@neosocial/protocol';
import { useState } from 'react';
import { useT } from '../i18n';
import { api } from '../lib/api';
import { failToast } from '../mod/actions';
import { updateData, useData } from '../store/data';
import { Seg, Switch } from '../ui/kit';
import { SetGroup, SetRow } from './Settings';

type Change = Partial<Omit<PrivacySettings, 'friend_requests_from'>> & {
  friend_requests_from?: Partial<PrivacySettings['friend_requests_from']>;
};

export function merged(p: PrivacySettings, c: Change): PrivacySettings {
  return {
    ...p,
    ...c,
    friend_requests_from: { ...p.friend_requests_from, ...c.friend_requests_from },
  };
}

export function PrivacySection() {
  const { t } = useT();
  const saved = useData((d) => d.settings?.privacy);
  // what the screen shows while a save is on its way; null = the server's value
  const [draft, setDraft] = useState<PrivacySettings | null>(null);
  const [busy, setBusy] = useState(false);
  if (!saved) {
    return (
      <>
        <h2>{t('um.privacy.title')}</h2>
        <p className="ns-muted">{t('um.privacy.unknown')}</p>
      </>
    );
  }
  const p = draft ?? saved;
  const save = async (c: Change) => {
    const next = merged(p, c);
    setDraft(next);
    setBusy(true);
    try {
      const s = await api.me.patchSettings({ privacy: next });
      updateData((d) => ({ ...d, settings: s }));
    } catch (e) {
      failToast(e);
    } finally {
      setDraft(null);
      setBusy(false);
    }
  };
  const pokeOptions = [
    { value: 'everyone' as const, label: t('um.privacy.pokes.everyone') },
    { value: 'friends' as const, label: t('um.privacy.friends') },
    { value: 'nobody' as const, label: t('um.privacy.nobody') },
  ];
  return (
    <>
      <h2>{t('um.privacy.title')}</h2>
      <SetGroup title={t('um.privacy.dms')}>
        <SetRow label={t('privacy.dmsFromMembers')}>
          <Switch
            checked={p.dms_from_members}
            disabled={busy}
            onChange={(v) => void save({ dms_from_members: v })}
            label={t('privacy.dmsFromMembers')}
          />
        </SetRow>
        <SetRow label={t('privacy.requests')} sub={t('um.privacy.requestsSub')}>
          <Switch
            checked={p.message_requests}
            disabled={busy}
            onChange={(v) => void save({ message_requests: v })}
            label={t('privacy.requests')}
          />
        </SetRow>
      </SetGroup>
      <SetGroup title={t('um.privacy.friendsFrom')}>
        {(
          [
            ['everyone', 'um.privacy.ff.everyone'],
            ['friends_of_friends', 'um.privacy.ff.fof'],
            ['server_members', 'um.privacy.ff.members'],
          ] as const
        ).map(([key, label]) => {
          // Everyone covers the other two (the server checks them only without it), as Discord shows it
          const covered = key !== 'everyone' && p.friend_requests_from.everyone;
          return (
            <SetRow key={key} label={t(label)} sub={covered ? t('set.privacy.covered') : undefined}>
              <Switch
                checked={covered || p.friend_requests_from[key]}
                disabled={busy || covered}
                onChange={(v) => void save({ friend_requests_from: { [key]: v } })}
                label={t(label)}
              />
            </SetRow>
          );
        })}
      </SetGroup>
      <SetGroup title={t('um.privacy.pokesGroup')}>
        <SetRow label={t('um.privacy.pokesFrom')}>
          <Seg<PokesFrom>
            label={t('um.privacy.pokesFrom')}
            value={p.pokes_from}
            onChange={(v) => !busy && void save({ pokes_from: v })}
            options={pokeOptions}
          />
        </SetRow>
      </SetGroup>
    </>
  );
}
